import { useEffect, useMemo, useState } from 'react';
import { getEventStartInstant, type TourEvent } from '@/features/events';
import { cx } from '@/lib/cx';
import styles from './Countdown.module.css';

export interface CountdownProps {
  event: TourEvent;
  /** Texto sobre el contador. */
  label?: string;
  className?: string;
}

interface Remaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function remainingFrom(targetMs: number, nowMs: number): Remaining | null {
  const diff = targetMs - nowMs;
  if (diff <= 0) return null;
  const seconds = Math.floor(diff / 1000);
  return {
    days: Math.floor(seconds / 86_400),
    hours: Math.floor((seconds % 86_400) / 3600),
    minutes: Math.floor((seconds % 3600) / 60),
    seconds: seconds % 60,
  };
}

const UNITS: ReadonlyArray<{ key: keyof Remaining; one: string; many: string }> = [
  { key: 'days', one: 'día', many: 'días' },
  { key: 'hours', one: 'hora', many: 'horas' },
  { key: 'minutes', one: 'minuto', many: 'minutos' },
  { key: 'seconds', one: 'segundo', many: 'segundos' },
];

/**
 * Cuenta atras hasta el ARRANQUE REAL del evento, en su propio huso horario.
 *
 * ADR-003 §4: la cuenta atras es legitima porque cuenta un hecho objetivo.
 * Este componente NO tiene, ni tendra, "plazas restantes": eso es escasez
 * falsa y esta prohibido. Si el evento ya empezo, se rotula honestamente.
 */
export function Countdown({ event, label = 'Faltan', className }: CountdownProps) {
  const targetMs = useMemo(() => getEventStartInstant(event).getTime(), [event]);

  // El unico estado es "que hora es". Lo que falta se DERIVA de el, asi que
  // cambiar de evento recalcula solo, sin un setState dentro del efecto (que
  // provoca renders en cascada y un primer frame con el dato viejo).
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const remaining = useMemo(() => remainingFrom(targetMs, now), [targetMs, now]);

  if (remaining === null) {
    return (
      <p className={cx(styles.countdown, styles.started, className)}>
        <span className={styles.label}>El encuentro de {event.city} ya ha comenzado</span>
      </p>
    );
  }

  const spoken = UNITS.map((u) => `${remaining[u.key]} ${remaining[u.key] === 1 ? u.one : u.many}`).join(
    ', ',
  );

  return (
    <div className={cx(styles.countdown, className)}>
      <p className={styles.label}>{label}</p>
      {/* El lector de pantalla oye una frase, no 4 numeros sueltos que cambian
       * cada segundo. `aria-hidden` en la rejilla evita el parloteo. */}
      <p className="az-visually-hidden">{`${label} ${spoken} para el encuentro de ${event.city}.`}</p>
      <div className={styles.units} aria-hidden="true">
        {UNITS.map((unit) => (
          <div className={styles.unit} key={unit.key}>
            <span className={styles.value}>{String(remaining[unit.key]).padStart(2, '0')}</span>
            <span className={styles.unitLabel}>{remaining[unit.key] === 1 ? unit.one : unit.many}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
